import * as Sentry from '@sentry/browser';
import 'gsap/PixiPlugin';
import { compose } from 'redux';
import { ItemIDs } from './Config/Items';
import { setGameSize } from './Config/Levels';
import Core, { gameResize } from './Core/Core';
import getLogger, { ILogger, setLogLevel } from './Core/Logger';
import './index.css';
import { lastAction } from './State/reducers';
import { IStuckArgs } from './State/reducers/level';
import { disableWebGL } from './Utils/Platform';

declare global {
    interface Window {
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__: typeof compose;
        reset: VoidFunction;
        sendRequestDebug: VoidFunction;
        MOJIDebugQA1: VoidFunction;
        MOJIDebugQA2: VoidFunction;
        MOJIDebugQA3: VoidFunction;
        MOJIDebugQA4: VoidFunction;
        MOJIDebugQA5: (collection: string, progress: number) => void;
        MOJIDebugQA6: (state: string) => void;
        MOJIDebugQA7: (item: ItemIDs) => void;
        MOJIDebugQA8: (day: number, month: number, year: number) => Promise<void>;
        MOJIDebugQA9: VoidFunction;
        MOJIDebugMissionRefresh: VoidFunction;
        MOJIDebugMissionComplete: (index: number) => void;
        MOJIDebugPassToLevel: (index: number) => void;
        MOJIDebugRefreshPrizeWheel: VoidFunction;
        MOJIDebugFailLevel: ({ reason, stuck }: IStuckArgs) => void;
        MOJIDebugForceJackpot: VoidFunction;
        MOJIDebugAddTime: (index: number) => void;
        MOJISetLogLevel: (int: number) => void;
        Stats: typeof import('stats.js');
        FBInstant?: typeof FBInstant;
        sgSdk?: any;
        runGameHelper?: any;
    }
}

declare global {
    const sgPlatform: string;
    const VERSION: string;
    const NODE_ENV: string;
    const DEBUG: boolean;
    const STATS: boolean;
    const SENTRY: boolean;
    const SUFFIX: string | boolean;
}

window.addEventListener('DOMContentLoaded', windowInit);

interface IGameConfig {
    orientation: string;
    game_width: number;
    game_height: number;
}

let app: PIXI.Application;
let timeout = 0;
const gameConfig: IGameConfig = {
    game_height: 1024,
    game_width: 576,
    orientation: 'portrait',
};
const logger: ILogger = getLogger('[INDEX]:');

function windowInit() {
    window.removeEventListener('DOMContentLoaded', windowInit);
    logger.debug('window init');
    if (DEBUG) {
        setLogLevel(-1);
    }
    if (SENTRY) {
        initSentry();
    }

    createApp();

    window.addEventListener('resize', resizeThrottle);
    // window.addEventListener('orientationchange', resizeThrottle);
    window.onresize = () => {
        resizeThrottle();
    };

    Core.init()
        .then(() => {
            logger.debug('core init success');
        })
        .catch(err => {
            logger.debug('error with core init: ' + err);
        });
}

function createApp() {
    gameConfig.orientation = window.orientation === 90 ? 'landscape' : 'portrait';
    setParentDivSize(gameConfig.game_width, gameConfig.game_height);

    const forceCanvas = disableWebGL();

    app = new PIXI.Application(gameConfig.game_width, gameConfig.game_height, {
        antialias: false,
        resolution: window.devicePixelRatio,
        sharedLoader: true,
        sharedTicker: true,
        preserveDrawingBuffer: false,
        clearBeforeRender: false,
        roundPixels: forceCanvas,
        forceCanvas,
    });

    app.renderer.autoResize = true;
    app.renderer.backgroundColor = 0x000000;

    getParentDiv().appendChild(app.view);
}

function sizer() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const newSize = setGameRatio(w, h);
    const newWidth = newSize.width;
    const newHeight = newSize.height;

    let offsetX = (newWidth - gameConfig.game_width) / 2;
    let offsetY = (newHeight - gameConfig.game_height) / 2;

    app.renderer.resize(newWidth * newSize.r, newHeight * newSize.r);
    setParentDivSize(newWidth * newSize.r, newHeight * newSize.r);

    setGameSize({
        gameHeight: newHeight,
        gameScale: newSize.r,
        gameWidth: newWidth,
        xOffset: offsetX,
        yOffset: offsetY,
    });
    gameResize();
    return {
        gameHeight: newHeight,
        gameScale: newSize.r,
        gameWidth: newWidth,
        xOffset: offsetX,
        yOffset: offsetY,
    };
}

function setGameRatio(w: number, h: number) {
    const gWidth = gameConfig.game_width;
    const gHeight = gameConfig.game_height;

    let scaleFactor = gWidth / w;
    let gameScale = w / gWidth;

    let gameWidth = gWidth;
    let gameHeight = h * scaleFactor;

    if (gameHeight <= gHeight) {
        scaleFactor = gHeight / h;
        gameScale = h / gHeight;
        gameWidth = w * scaleFactor;
        gameHeight = gHeight;
    }

    return { width: gameWidth, height: gameHeight, r: gameScale };
}

let dispatched = false;

function resizeThrottle() {
    if (timeout) {
        dispatched = false;
    } else {
        dispatched = true;
        sizer();
        timeout = window.setTimeout(() => {
            timeout = 0;
            window.clearTimeout(timeout);
            if (!dispatched) {
                resizeThrottle();
            }
        }, 100);
    }
}

function initSentry() {
    Sentry.init({
        dsn: 'https://b156a5f3c1144ae7adadc1f1c095f2e0@sentry.io/1334163',
        release: 'moji-' + VERSION,
        beforeSend(event, hint?) {
            try {
                event.extra = {
                    'redux-state': Core.store.getState(),
                    'last-action': lastAction,
                };
            } catch (err) {
                event.extra = {
                    'redux-state': { message: 'State not available.', error: err },
                };
            }
            return event;
        },
    });
}

function getParentDiv(): HTMLDivElement {
    return document.getElementById('app') as HTMLDivElement;
}

function setParentDivSize(width: number, height: number): void {
    const parentDiv = getParentDiv();
    parentDiv.style.width = width.toString() + 'px';
    parentDiv.style.height = height.toString() + 'px';
}

export { app, app as default, sizer, gameConfig };
